import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeCheck,
  Copy,
  Sparkles,
  Ticket,
  Wallet,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { PageHeader } from "@/components/shared";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { useActivateMemberMutation, useMemberProfile } from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import { kycStatusMeta } from "@/lib/account-profile-utils";
import { CustomerKycStatus } from "@/types/customer.dto";

const AccountMemberId = () => {
  const { toast } = useToast();
  const { data: memberProfile, isLoading } = useMemberProfile();
  const activateMember = useActivateMemberMutation();

  const handleActivate = async () => {
    try {
      const result = await activateMember.mutateAsync();
      toast({
        title: "Member ID activated!",
        description: `Your ID: ${result.memberCode}. ${result.unusedCouponCount} coupon(s) added to your wallet.`,
      });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const copyMemberCode = () => {
    if (!memberProfile?.memberCode) return;
    navigator.clipboard.writeText(memberProfile.memberCode);
    toast({ title: "Copied to clipboard" });
  };

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <PageHeader
          title="Member ID"
          description="Activate your Member ID to unlock wallet coupons, P2P transfers, and B.V rewards."
        />
        <Skeleton className="h-48 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  const kycMeta = kycStatusMeta[memberProfile?.kycStatus ?? CustomerKycStatus.NotSubmitted];
  const isActive = memberProfile?.isMemberActive ?? false;
  const isPrime = memberProfile?.isPrimeMember ?? false;
  const personalBv = memberProfile?.personalBv ?? 0;
  const activationThreshold = memberProfile?.activationBvThreshold ?? 0;
  const bvProgress =
    activationThreshold > 0 ? Math.min(100, (personalBv / activationThreshold) * 100) : 0;
  const remainingBv = Math.max(0, activationThreshold - personalBv);
  const canActivate = Boolean(memberProfile?.kycVerified && !isActive);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Member ID"
        description="Activate your Member ID to unlock wallet coupons, P2P transfers, and B.V rewards."
      />

      {/* Member card */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-primary via-primary/90 to-primary/70 p-6 text-primary-foreground shadow-xl sm:p-8">
        <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-12 -left-8 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
        <div className="relative space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground/70">
                Bihari Mall Member
              </p>
              <p className="mt-3 font-mono text-2xl font-bold tracking-wider sm:text-3xl">
                {isActive && memberProfile?.memberCode ? memberProfile.memberCode : "•••• •••• ••••"}
              </p>
            </div>
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/15 backdrop-blur-sm">
              <Ticket className="h-6 w-6" />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Badge className="border-0 bg-white/20 text-primary-foreground hover:bg-white/20">
              {isActive ? "Active" : "Inactive"}
            </Badge>
            <Badge className="border-0 bg-white/20 text-primary-foreground hover:bg-white/20">
              {isPrime ? "Prime" : "Not Prime"}
            </Badge>
            {isActive && memberProfile?.memberActivatedAt && (
              <span className="text-xs text-primary-foreground/80">
                Activated {new Date(memberProfile.memberActivatedAt).toLocaleDateString("en-IN")}
              </span>
            )}
            {isActive && memberProfile?.memberCode && (
              <Button
                size="sm"
                variant="secondary"
                className="ml-auto gap-1.5 bg-white/20 text-primary-foreground hover:bg-white/30"
                onClick={copyMemberCode}
              >
                <Copy className="h-3.5 w-3.5" />
                Copy ID
              </Button>
            )}
          </div>
        </div>
      </div>

      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Personal BV progress</CardTitle>
          <CardDescription>
            Member ID activates automatically from delivered order BV when you reach the threshold.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <p className="text-3xl font-bold text-foreground">{personalBv.toFixed(0)} BV</p>
              <p className="text-sm text-muted-foreground">
                of {activationThreshold.toFixed(0)} BV activation threshold
              </p>
            </div>
            <Badge variant={isActive ? "default" : "secondary"}>
              {isActive ? "Activation complete" : `${remainingBv.toFixed(0)} BV to go`}
            </Badge>
          </div>
          <Progress value={bvProgress} className="h-3" />
          <p className="text-sm text-muted-foreground">
            Delivered orders add to your Personal BV. Once your Personal BV reaches the threshold,
            your Member ID becomes Active and Prime automatically.
          </p>
        </CardContent>
      </Card>

      {/* Status steps */}
      <Card className="border-border shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Member benefits</CardTitle>
          <CardDescription>
            BV activates membership automatically; KYC remains available for withdrawals.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start gap-4 rounded-xl border border-border p-4">
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                memberProfile?.kycVerified ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"
              }`}
            >
              <BadgeCheck className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <p className="font-semibold text-foreground">KYC for withdrawals</p>
                <Badge variant={kycMeta.variant}>{kycMeta.label}</Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                {kycMeta.description} KYC is not required for Personal BV auto-activation.
              </p>
              {!memberProfile?.kycVerified && (
                <Button variant="link" className="mt-1 h-auto p-0" asChild>
                  <Link to="/account/kyc">
                    Go to KYC
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
              )}
            </div>
          </div>

          <div className="flex items-start gap-4 rounded-xl border border-border p-4">
            <div
              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                isActive ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"
              }`}
            >
              <Sparkles className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-foreground">Member ID activation</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Your Member ID activates automatically when delivered orders take your Personal BV
                to {activationThreshold.toFixed(0)} BV. Verified KYC customers can also activate manually.
              </p>
              {canActivate && (
                <Button
                  className="mt-3 gap-2"
                  onClick={handleActivate}
                  disabled={activateMember.isPending}
                >
                  <BadgeCheck className="h-4 w-4" />
                  {activateMember.isPending ? "Activating…" : "Activate Member ID"}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {isActive && (
        <div className="grid gap-4 sm:grid-cols-2">
          <Card className="border-border shadow-sm">
            <CardContent className="flex items-center gap-4 p-5">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <Wallet className="h-5 w-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {memberProfile?.unusedCouponCount ?? 0}
                </p>
                <p className="text-xs text-muted-foreground">Unused coupons</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm text-muted-foreground">
                Use your Member ID for P2P wallet transfers and to earn B.V rewards on purchases.
              </p>
              <Button variant="outline" size="sm" className="mt-3" asChild>
                <Link to="/account/wallet">Open wallet</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {!memberProfile?.kycVerified && memberProfile?.kycStatus === CustomerKycStatus.NotSubmitted && (
        <Alert>
          <AlertDescription>
            KYC is only needed for withdrawal readiness, not BV auto-activation. Head to the{" "}
            <Link to="/account/kyc" className="font-medium text-primary hover:underline">
              KYC page
            </Link>{" "}
            to submit your documents.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default AccountMemberId;
